import Immutable from 'immutable';
import { BookingStates } from 'constants/booking/states';
import _ from 'underscore';

const initialState = Immutable.Map();
let reducers;
let perStateReducers;

// Single Booking Slot Record reducers
function bookingSlotLoadingStarted(state, payload) {
  if (
    payload.search !== null ||
    payload.filter !== null ||
    payload.order !== null
  ) {
    state.setIn(['records', payload.id.toString(), 'searching'], true);
  } else {
    state.setIn(['records', payload.id.toString(), 'loading'], true);
  }

  return state;
}
function bookingSlotLoadingCompleted(state, payload) {
  if (payload.state_labels) {
    state.setIn(
      ['records', payload.id.toString(), 'stateLabels'],
      Immutable.fromJS(payload.state_labels)
    );
  }
  delete payload.state_labels;
  state.setIn(['records', payload.id.toString(), 'searching'], false);
  state.setIn(['records', payload.id.toString(), 'loading'], false);
  state.setIn(
    ['records', payload.id.toString(), 'record'],
    Immutable.fromJS(payload)
  );
  state.setIn(['records', 'offset'], payload.offset);
  state.setIn(['records', 'limit'], payload.limit);
  if (payload.offset != null && payload.limit != null) {
    state.setIn(['records', 'nextOffset'], payload.offset + payload.limit);
  }

  return state;
}
function bookingSlotLoadingCached(state, payload) {
  state.setIn(['records', payload.id.toString(), 'searching'], false);
  state.setIn(['records', payload.id.toString(), 'loading'], false);
  return state;
}
function bookingSlotLoadingFailed(state, payload) {
  state.setIn(['records', payload.id.toString(), 'searching'], false);
  state.setIn(['records', payload.id.toString(), 'loading'], false);
  return state;
}

function saveBookingSlotFilters(
  state,
  { id, page = null, search = null, currentFilter = null, savedSearch = null }
) {
  if (page) state.setIn(['records', id.toString(), 'page'], page);
  if (search !== null)
    state.setIn(['records', id.toString(), 'search'], search);
  if (savedSearch !== null)
    state.setIn(['records', id.toString(), 'savedSearch'], savedSearch);
  if (currentFilter)
    state.setIn(['records', id.toString(), 'currentFilter'], currentFilter);

  return state;
}

function saveBookingSlotOrder(state, { id, order = null }) {
  if (order) {
    const orderValue = typeof order === 'string' ? order : order.value;
    state.setIn(['records', id.toString(), 'order'], orderValue);
  }
  return state;
}

function storePreviousBookingSlot(state, payload) {
  return state.set('previousBookingSlot', payload);
}

function clearPreviousBookingSlot(state, payload) {
  return state.set('previousBookingSlot', null);
}

function bookingSlotUpdateStarted(state, payload) {
  state.setIn(['records', payload.id.toString(), 'update', 'completed'], false);
  state.setIn(['records', payload.id.toString(), 'update', 'failed'], false);
  return state.setIn(
    ['records', payload.id.toString(), 'update', 'loading'],
    true
  );
}

function bookingSlotUpdateCompleted(state, payload) {
  state.setIn(['records', payload.id.toString(), 'update', 'failed'], false);
  state.setIn(['records', payload.id.toString(), 'update', 'loading'], false);
  const bookingsNextOffset = state.getIn([
    'records',
    payload.id.toString(),
    'record',
    'bookings_next_offset',
  ]);
  const bookingsPreviousOffset = state.getIn([
    'records',
    payload.id.toString(),
    'record',
    'bookings_previous_offset',
  ]);
  const bookings = state.getIn([
    'records',
    payload.id.toString(),
    'record',
    'bookings',
  ]);
  state.setIn(
    ['records', payload.id.toString(), 'record'],
    Immutable.fromJS(payload)
  );
  state.setIn(
    ['records', payload.id.toString(), 'record', 'bookings_next_offset'],
    bookingsNextOffset
  );
  state.setIn(
    ['records', payload.id.toString(), 'record', 'bookings_previous_offset'],
    bookingsPreviousOffset
  );
  state.setIn(
    ['records', payload.id.toString(), 'record', 'bookings'],
    bookings
  );
  return state.setIn(
    ['records', payload.id.toString(), 'update', 'completed'],
    true
  );
}

function bookingSlotUpdateFailed(state, payload) {
  state.setIn(['records', payload.id.toString(), 'update', 'completed'], false);
  state.setIn(['records', payload.id.toString(), 'update', 'loading'], false);
  return state.setIn(
    ['records', payload.id.toString(), 'update', 'failed'],
    true
  );
}

// Per-bookingState reducers. ie. "pending", "confirmed", ...
// They take `bookingState` bound as first param
function slotsLoadingStarted(bookingState, state) {
  return state.setIn([bookingState, 'loading'], true);
}

function slotsLoadingFailed(bookingState, state) {
  state.setIn([bookingState, 'error'], true);
  return stopSlotsLoading(bookingState, state);
}

function slotsLoadingCompleted(bookingState, state, payload) {
  state.setIn([bookingState, 'allRecordsLoaded'], false);
  state.setIn([bookingState, 'slots'], Immutable.fromJS(payload));
  return stopSlotsLoading(bookingState, state);
}

function slotsLoadingMoreCompleted(bookingState, state, payload) {
  let slots;

  if (payload.length > 0) {
    slots = state.getIn([bookingState, 'slots']);
    state.setIn([bookingState, 'slots'], slots.push(...payload));
  } else {
    state.setIn([bookingState, 'allRecordsLoaded'], true);
  }

  return stopSlotsLoading(bookingState, state);
}

function slotsLoadingCached(bookingState, state) {
  return stopSlotsLoading(bookingState, state);
}

function stopSlotsLoading(bookingState, state) {
  return state.setIn([bookingState, 'loading'], false);
}

function upcomingSlotsLoadingStarted(state, payload) {
  return state
    .set('loading', true)
    .setIn(['upcoming', 'completed'], false)
    .setIn(['upcoming', 'failed'], false);
}

function upcomingSlotsLoadingCompleted(state, payload) {
  return state
    .set('loading', false)
    .setIn(['upcoming', 'completed'], true)
    .setIn(['upcoming', 'records'], payload)
    .setIn(['upcoming', 'failed'], false);
}

function upcomingSlotsLoadingFailed(state, payload) {
  return state
    .set('loading', false)
    .setIn(['upcoming', 'completed'], false)
    .setIn(['upcoming', 'failed'], true);
}

function upcomingSlotsLoadingCached(state, payload) {
  return state
    .set('loading', false)
    .setIn(['upcoming', 'completed'], true)
    .setIn(['upcoming', 'failed'], false);
}

function inProgressSlotsLoadingStarted(state, payload) {
  return state
    .set('loading', true)
    .setIn(['inProgress', 'completed'], false)
    .setIn(['inProgress', 'failed'], false);
}

function inProgressSlotsLoadingCompleted(state, payload) {
  payload.reverse();
  return state
    .set('loading', false)
    .setIn(['inProgress', 'completed'], true)
    .setIn(['inProgress', 'records'], payload)
    .setIn(['inProgress', 'failed'], false);
}

function inProgressSlotsLoadingFailed(state, payload) {
  return state
    .set('loading', false)
    .setIn(['inProgress', 'completed'], false)
    .setIn(['inProgress', 'failed'], true);
}

function inProgressSlotsLoadingCached(state, payload) {
  return state
    .set('loading', false)
    .setIn(['inProgress', 'completed'], true)
    .setIn(['inProgress', 'failed'], false);
}

function guestSlotsLoadingStarted(state, payload) {
  return state
    .set('loading', true)
    .setIn(['forGuest', 'completed'], false)
    .setIn(['forGuest', 'failed'], false);
}

function guestSlotsLoadingCompleted(state, payload) {
  return state
    .set('loading', false)
    .setIn(['forGuest', 'completed'], true)
    .setIn(['forGuest', 'records'], payload)
    .setIn(['forGuest', 'failed'], false);
}

function guestSlotsLoadingFailed(state, payload) {
  return state
    .set('loading', false)
    .setIn(['forGuest', 'completed'], false)
    .setIn(['forGuest', 'failed'], true);
}

function guestSlotsLoadingCached(state, payload) {
  return state
    .set('loading', false)
    .setIn(['forGuest', 'completed'], true)
    .setIn(['forGuest', 'failed'], false);
}

function resetGuestBookingSlots(state, payload) {
  return state
    .set('loading', false)
    .setIn(['forGuest', 'completed'], true)
    .setIn(['forGuest', 'records'], [])
    .setIn(['forGuest', 'failed'], false);
}

function slotUpdateBookingCheckinsForSlot(
  state,
  { bookingId, slotId, checkins, numCheckins, numCheckinsAddons }
) {
  const bookings = state.getIn([
    'records',
    slotId.toString(),
    'record',
    'bookings',
  ]);
  const bookingIndex = bookings.findIndex((b) => b.get('id') === bookingId);

  state.setIn(
    ['records', slotId.toString(), 'record', 'total_guest_checkins'],
    numCheckins
  );
  state.setIn(
    ['records', slotId.toString(), 'record', 'total_addon_checkins'],
    numCheckinsAddons
  );
  state.setIn(
    [
      'records',
      slotId.toString(),
      'record',
      'bookings',
      bookingIndex,
      'checkins',
    ],
    checkins
  );
  return state;
}

perStateReducers = BookingStates.reduce((prev, type) => {
  const TYPE = type.toUpperCase();
  return _.extend(prev, {
    [`LOAD_${TYPE}_BOOKING_SLOTS`]: slotsLoadingStarted.bind(null, type),
    [`LOAD_${TYPE}_BOOKING_SLOTS_COMPLETED`]: slotsLoadingCompleted.bind(
      null,
      type
    ),
    [`LOAD_MORE_${TYPE}_BOOKING_SLOTS_COMPLETED`]:
      slotsLoadingMoreCompleted.bind(null, type),
    [`LOAD_${TYPE}_BOOKING_SLOTS_CACHED`]: slotsLoadingCached.bind(null, type),
    [`LOAD_${TYPE}_BOOKING_SLOTS_FAILED`]: slotsLoadingFailed.bind(null, type),
  });
}, {});

reducers = _.extend(
  {
    LOAD_BOOKING_SLOT: bookingSlotLoadingStarted,
    LOAD_BOOKING_SLOT_COMPLETED: bookingSlotLoadingCompleted,
    LOAD_BOOKING_SLOT_CACHED: bookingSlotLoadingCached,
    LOAD_BOOKING_SLOT_FAILED: bookingSlotLoadingFailed,

    LOAD_UPCOMING_BOOKING_SLOTS: upcomingSlotsLoadingStarted,
    LOAD_UPCOMING_BOOKING_SLOTS_COMPLETED: upcomingSlotsLoadingCompleted,
    LOAD_UPCOMING_BOOKING_SLOTS_CACHED: upcomingSlotsLoadingCached,
    LOAD_UPCOMING_BOOKING_SLOTS_FAILED: upcomingSlotsLoadingFailed,

    LOAD_IN_PROGRESS_BOOKING_SLOTS: inProgressSlotsLoadingStarted,
    LOAD_IN_PROGRESS_BOOKING_SLOTS_COMPLETED: inProgressSlotsLoadingCompleted,
    LOAD_IN_PROGRESS_BOOKING_SLOTS_CACHED: inProgressSlotsLoadingCached,
    LOAD_IN_PROGRESS_BOOKING_SLOTS_FAILED: inProgressSlotsLoadingFailed,

    // Search-related. Slots for a specific guests
    LOAD_GUEST_BOOKING_SLOTS: guestSlotsLoadingStarted,
    LOAD_GUEST_BOOKING_SLOTS_COMPLETED: guestSlotsLoadingCompleted,
    LOAD_GUEST_BOOKING_SLOTS_CACHED: guestSlotsLoadingCached,
    LOAD_GUEST_BOOKING_SLOTS_FAILED: guestSlotsLoadingFailed,
    RESET_GUEST_BOOKING_SLOTS: resetGuestBookingSlots,

    UPDATE_BOOKING_SLOT: bookingSlotUpdateStarted,
    UPDATE_BOOKING_SLOT_COMPLETED: bookingSlotUpdateCompleted,
    UPDATE_BOOKING_SLOT_FAILED: bookingSlotUpdateFailed,

    UPDATE_BOOKING_CHECKINS_FOR_SLOT: slotUpdateBookingCheckinsForSlot,

    SAVE_BOOKING_SLOT_FILTERS: saveBookingSlotFilters,
    SAVE_BOOKING_SLOT_ORDER: saveBookingSlotOrder,

    STORE_PREVIOUS_BOOKING_SLOT: storePreviousBookingSlot,
    CLEAR_PREVIOUS_BOOKING_SLOT: clearPreviousBookingSlot,
  },
  perStateReducers
);

export default function reducer(state = initialState, { type, payload }) {
  return state.withMutations((state) => {
    return (reducers[type] || (() => state))(state, payload);
  });
}
